/* eslint-disable max-lines */
import {type ChangeEvent, Component, createRef, type MouseEvent, type TouchEvent} from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import dragster from 'utils/dragster';
import Constants from 'utils/constants';
import DelayedAction from 'utils/delayed_action';
import {t} from 'utils/i18n';
import {isIosChrome, isMobileApp} from 'utils/user_agent';
import {getTable} from 'utils/paste';
import {
    clearFileInput,
    cmdOrCtrlPressed,
    isKeyPressed,
    generateId,
    isFileTransfer,
    isUriDrop,
    localizeMessage,
} from 'utils/utils';

import MenuWrapper from 'components/widgets/menu/menu_wrapper';
import Menu from 'components/widgets/menu/menu';

import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import type {FileUploadResponse} from '@mattermost/types/files';

import {type ServerError} from '@mattermost/types/errors';

import {PaperclipDiagonalNewIcon} from '@time-webkit/all/icons/paperclip-diagonal-new';
import {ActionButton} from 'packages/editor/src/components/action-button';
import {ShortcutTooltip} from 'packages/editor/src/components/shortcut-tooltip';
import {sendToStatist} from 'packages/statist';

import type {Props, State} from './types';

const holders = defineMessages({
    limited: {
        id: t('file_upload.limited'),
        defaultMessage: 'You can add up to {count} attachments',
    },
    filesAbove: {
        id: t('file_upload.filesAbove'),
        defaultMessage: 'You can add attachments up to {max} MB',
    },
    zeroBytesFiles: {
        id: t('file_upload.zeroBytesFiles'),
        defaultMessage: 'You are uploading empty files: {filenames}',
    },
    zeroBytesFile: {
        id: t('file_upload.zeroBytesFile'),
        defaultMessage: 'You are uploading an empty file: {filename}',
    },
    pasted: {
        id: t('file_upload.pasted'),
        defaultMessage: 'File Pasted at ',
    },
    uploadFile: {
        id: t('file_upload.upload_files'),
        defaultMessage: 'Upload files',
    },
});

const OVERLAY_TIMEOUT = 500;
const CLEAR_ERROR_TIMEOUT = 15_000;

const customStyles = {
    left: 'inherit',
    right: 0,
    bottom: '100%',
    top: 'auto',
};

// eslint-disable-next-line react/require-optimization
export class FileUpload extends Component<Props, State> {
    static defaultProps: Partial<Props> = {
        pluginFileUploadMethods: [],
        pluginFilesWillUploadHooks: [],
        postAction: 'create',
    };

    state: State = {abortControllers: {}};

    fileInput = createRef<HTMLInputElement>();

    unbindDragsterEvents: (() => void) | null = null;

    uploadClientErrorTimeoutId: ReturnType<typeof setTimeout> | null = null;

    abortController: AbortController | null = null;

    componentDidMount() {
        if (this.props.postAction === 'create') {
            this.registerDragAndDrop();
        }

        this.abortController = new AbortController();
        document.addEventListener('paste', this.pasteUpload, {signal: this.abortController.signal});
        document.addEventListener('keydown', this.keyUpload, {signal: this.abortController.signal});
        this.fileInput.current?.addEventListener('cancel', () => this.props.onReturnFocus?.(), {
            signal: this.abortController.signal,
        });
    }

    registerDragAndDrop() {
        if (this.props.postType === 'post') {
            this.registerDragEvents('.row.main', '.center-file-overlay');
        }

        if (this.props.postType === 'comment') {
            this.registerDragEvents('.post-right__container', '.right-file-overlay');
        }

        if (this.props.postType === 'thread') {
            this.registerDragEvents('.ThreadPane', '.right-file-overlay');
        }
    }

    componentWillUnmount() {
        this.abortController?.abort();
        this.abortController = null;
        this.unbindDragsterEvents?.();
        this.clearClientErrorTimeout();
    }

    fileUploadSuccess = (data: FileUploadResponse, channelId: string, currentRootId?: string) => {
        if (data) {
            sendToStatist('editor.attachment.complete');
            this.props.onFileUpload?.(data.file_infos, data.client_ids, channelId, currentRootId);

            const abortControllers = Object.assign({}, this.state.abortControllers);

            data.client_ids.forEach((id) => {
                Reflect.deleteProperty(abortControllers, id);
            });

            this.setState({abortControllers});
        }
    };

    clearClientErrorTimeout = () => {
        if (this.uploadClientErrorTimeoutId) {
            clearTimeout(this.uploadClientErrorTimeoutId);
            this.uploadClientErrorTimeoutId = null;
        }
    }

    clearErrors = () => {
        this.clearClientErrorTimeout();
        this.props.onUploadServerError?.(null);
        this.props.onUploadClientError?.(null);
    };

    handleServerUploadError = (error: string | ServerError, clientId?: string, channelId?: string, currentRootId?: string) => {
        this.clearClientErrorTimeout();
        this.props.onUploadServerError?.(error, clientId, channelId, currentRootId);
    };

    handleClientUploadError = (error: string) => {
        this.clearClientErrorTimeout();
        this.props.onUploadClientError?.(new Error(error));

        this.uploadClientErrorTimeoutId = setTimeout(() => {
            this.uploadClientErrorTimeoutId = null;
            this.props.onUploadClientError?.(null);
        }, CLEAR_ERROR_TIMEOUT);
    };

    pluginUploadFiles = (files: FileList) => {
        this.clearErrors();
        this.uploadFiles(files);
    };

    checkPluginHooksAndUploadFiles = (files: FileList | File[]) => {
        this.clearErrors();

        let sortedFiles = Array.from(files).sort((a, b) =>
            a.name.localeCompare(b.name, this.props.locale, {numeric: true}),
        );

        if (this.props.pluginFilesWillUploadHooks) {
            for (const h of this.props.pluginFilesWillUploadHooks) {
                const result = h.hook(sortedFiles, this.pluginUploadFiles);

                // Display an error message if there is one but don't reject the upload
                if (result.message) {
                    this.handleServerUploadError(result.message);
                }

                sortedFiles = result.files as File[];
            }
        }

        if (sortedFiles) {
            this.uploadFiles(sortedFiles);
        }
    };

    uploadFiles = (files: File[] | FileList) => {
        const {channelId, rootId} = this.props;

        const sortedFiles = Array.from(files);

        const uploadsRemaining = Constants.MAX_UPLOAD_FILES - this.props.fileCount;
        let numUploads = 0;

        // keep track of how many files have been too large
        const tooLargeFiles: File[] = [];
        const zeroFiles: File[] = [];
        const clientIds: string[] = [];

        for (let i = 0; i < sortedFiles.length && numUploads < uploadsRemaining; i++) {
            const file = sortedFiles[i];

            if (!file) {
                continue;
            }

            if (file.size > this.props.maxFileSize) {
                tooLargeFiles.push(file);
                continue;
            }

            if (file.size === 0) {
                zeroFiles.push(file);
            }

            // generate a unique id that can be used by other components to refer back to this upload
            const clientId = generateId();

            const abortController = this.props.actions.uploadFile({
                file,
                channelId,
                rootId,
                clientId,
                onProgress: (percent) => this.props.onUploadProgress({
                    clientId,
                    name: file.name,
                    percent,
                    type: file.type,
                }),
                onError: (error) => this.handleServerUploadError(error, clientId, channelId, rootId),
                onSuccess: (data) => this.fileUploadSuccess(data, channelId, rootId),
            });

            this.setState((prevState) => ({
                ...prevState,
                abortControllers: {
                    ...prevState.abortControllers,
                    [clientId]: abortController,
                },
            }));
            clientIds.push(clientId);

            numUploads += 1;
        }

        this.props.onUploadStart?.(clientIds, channelId);

        const {formatMessage} = this.props.intl;
        const errors = [];

        if (sortedFiles.length > uploadsRemaining) {
            errors.push(formatMessage(holders.limited, {count: Constants.MAX_UPLOAD_FILES}));
        } else if (tooLargeFiles.length > 0) {
            errors.push(
                formatMessage(holders.filesAbove, {
                    max: this.props.maxFileSize / 1048576,
                }),
            );
        }

        if (zeroFiles.length > 1) {
            const zeroFilenames = zeroFiles.map((file) => file.name).join(', ');

            errors.push(formatMessage(holders.zeroBytesFiles, {filenames: zeroFilenames}));
        } else if (zeroFiles.length > 0) {
            errors.push(formatMessage(holders.zeroBytesFile, {filename: zeroFiles[0]!.name}));
        }

        if (errors.length > 0) {
            this.handleClientUploadError(errors.join('. '));
        }
    };

    handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target?.files?.length) {
            this.checkPluginHooksAndUploadFiles(event.target.files);

            clearFileInput(event.target);
        }

        this.props.onFileUploadChange?.();
    };

    handleDrop = (event: DragEvent) => {
        const {formatMessage} = this.props.intl;
        if (!this.props.canUploadFiles) {
            this.handleClientUploadError(formatMessage({id: 'file_upload.disabled', defaultMessage: 'File attachments are disabled.'}));
            return;
        }

        this.clearErrors();

        const dataTransfer = event.dataTransfer;

        const items = dataTransfer?.items || [];
        const droppedFiles = dataTransfer?.files || [];

        const files: File[] = [];
        Array.from(droppedFiles).forEach((file, index) => {
            const item = items[index];
            if (
                item &&
                item.webkitGetAsEntry &&
                (item.webkitGetAsEntry() === null || item.webkitGetAsEntry()?.isDirectory)
            ) {
                return;
            }
            files.push(file);
        });

        const types = dataTransfer?.types;

        if (types && dataTransfer) {
            if (isUriDrop(dataTransfer)) {
                return;
            }

            // For non-IE browsers
            if (types.includes && !types.includes('Files')) {
                return;
            }
        }

        if (files.length === 0) {
            this.handleClientUploadError(
                localizeMessage(
                    'file_upload.drag_folder',
                    'Folders cannot be uploaded. Please drag all files separately.',
                ),
            );
            return;
        }

        if (files.length) {
            this.checkPluginHooksAndUploadFiles(files);
        }

        this.props.onFileUploadChange?.();
    };

    registerDragEvents = (containerSelector: string, overlaySelector: string) => {
        const overlay = document.querySelector(overlaySelector);

        const dragTimeout = new DelayedAction(() => {
            overlay?.classList.add('hidden');
        });

        let dragsterActions;

        if (this.props.canUploadFiles) {
            dragsterActions = {
                enter(event: CustomEvent) {
                    const files = event.detail.dataTransfer;
                    if (!isUriDrop(files) && isFileTransfer(files)) {
                        overlay?.classList.remove('hidden');
                    }
                },
                leave(event: CustomEvent) {
                    const files = event.detail.dataTransfer;

                    if (!isUriDrop(files) && isFileTransfer(files)) {
                        overlay?.classList.add('hidden');
                    }

                    dragTimeout.cancel();
                },
                over() {
                    dragTimeout.fireAfter(OVERLAY_TIMEOUT);
                },
                drop: (event: CustomEvent) => {
                    overlay?.classList.add('hidden');
                    dragTimeout.cancel();

                    this.handleDrop(event.detail);
                },
            };
        } else {
            dragsterActions = {
                drop: (event: CustomEvent) => {
                    this.handleDrop(event.detail);
                },
            };
        }

        this.unbindDragsterEvents = dragster(containerSelector, dragsterActions);
    };

    containsEventTarget = (targetElement: Element | Text, eventTarget: EventTarget | null) =>
        targetElement && targetElement.contains(eventTarget as Node | null);

    pasteUpload = (event: ClipboardEvent) => {
        const {formatMessage} = this.props.intl;

        if (!event.clipboardData || !event.clipboardData.items || getTable(event.clipboardData)) {
            return;
        }

        const textarea = this.props.getTarget();

        if (textarea && !this.containsEventTarget(textarea, event.target)) {
            return;
        }

        this.clearErrors();

        const items = [];
        for (let i = 0; i < event.clipboardData.items.length; i++) {
            const item = event.clipboardData.items[i]!;

            if (item.kind !== 'file') {
                continue;
            }

            items.push(item);
        }

        if (items && items.length > 0) {
            // Rich text can be pasted as image
            for (let i = 0; i < event.clipboardData.items.length; i++) {
                const item = event.clipboardData.items[i]!;
                if (item.kind === 'string' && item.type === 'text/html') {
                    const html = String(event.clipboardData.getData(item.type)).toLowerCase();
                    if (html.includes('<img src=')) {
                        continue;
                    }

                    // This item will be handled by create_post / create_comment
                    return;
                }
            }

            if (!this.props.canUploadFiles) {
                this.handleClientUploadError(
                    formatMessage({
                        id: 'file_upload.disabled',
                        defaultMessage: 'File attachments are disabled.',
                    }),
                );
                return;
            }

            event.preventDefault();

            const files: File[] = [];

            items.forEach((item) => {
                const file = item.getAsFile();

                if (!file) {
                    return;
                }

                if (file.name) {
                    files.push(file);
                    return;
                }

                const d = new Date();

                const hour = d.getHours() < 10 ? `0${d.getHours()}` : `${d.getHours()}`;

                const minute = d.getMinutes() < 10 ? `0${d.getMinutes()}` : `${d.getMinutes()}`;

                let ext = '';

                if (file.name) {
                    if (file.name.includes('.')) {
                        ext = `.${file.name.split('.').pop()}`;
                    }
                } else if (item.type.includes('/')) {
                    ext = '.' + item.type.split('/')[1]!.toLowerCase();
                }

                const defaultName = `${formatMessage(holders.pasted)}${d.getFullYear()}-${
                    d.getMonth() + 1
                }-${d.getDate()}-${hour}-${minute}${ext}`;

                const newFile = new File([file], defaultName, {
                    type: file.type,
                });

                files.push(newFile);
            });

            if (files.length > 0) {
                this.checkPluginHooksAndUploadFiles(files);
                this.props.onFileUploadChange?.();
            }
        }
    };

    keyUpload = (event: KeyboardEvent) => {
        const textarea = this.props.getTarget();

        if (textarea && !this.containsEventTarget(textarea, event.target)) {
            return;
        }

        const {formatMessage} = this.props.intl;

        if (cmdOrCtrlPressed(event) && isKeyPressed(event, Constants.KeyCodes.U) && !event.shiftKey) {
            event.preventDefault();

            if (!this.props.canUploadFiles) {
                this.handleClientUploadError(
                    formatMessage({
                        id: 'file_upload.disabled',
                        defaultMessage: 'File attachments are disabled.',
                    }),
                );
                return;
            }

            const postTextbox = this.props.postType === 'post' && document.activeElement?.id === 'post_textbox';
            let threadTextbox = this.props.postType === 'thread' && document.activeElement?.id === 'reply_textbox';
            let commentTextbox = this.props.postType === 'comment' && document.activeElement?.id === 'reply_textbox';
            const editTextbox = this.props.postAction === 'edit' && document.activeElement?.id === 'edit_textbox';

            // Хак, который позволяет исправить проблему загрузки файлов по хоткею Mod+U для нового редактора
            if (this.props.newEditorEnabled) {
                threadTextbox = this.props.postType === 'thread' && document.activeElement?.id === 'post_textbox';
                commentTextbox = this.props.postType === 'comment' && document.activeElement?.id === 'post_textbox';
                sendToStatist('editor.attachment.tap', {hotkey: true});
            }

            if (postTextbox || commentTextbox || editTextbox || threadTextbox) {
                this.fileInput.current?.focus();
                this.fileInput.current?.click();
            }
        }
    };

    cancelUpload = (clientId: string) => {
        const abortControllers = Object.assign({}, this.state.abortControllers);
        const abortController = abortControllers[clientId];

        if (abortController) {
            abortController.abort();

            Reflect.deleteProperty(abortControllers, clientId);
            this.setState({abortControllers});
        }
    };

    handleMaxUploadReached = (event?: MouseEvent<HTMLInputElement>) => {
        event?.preventDefault?.();

        const {formatMessage} = this.props.intl;

        this.handleClientUploadError(formatMessage(holders.limited, {count: Constants.MAX_UPLOAD_FILES}));
    };

    handleLocalFileUploaded = (event: MouseEvent<HTMLInputElement>) => {
        const uploadsRemaining = Constants.MAX_UPLOAD_FILES - this.props.fileCount;
        if (uploadsRemaining > 0) {
            if (this.props.onClick) {
                this.props.onClick();
            }
        } else {
            this.handleMaxUploadReached(event);
        }
    };

    simulateInputClick = (event: MouseEvent | TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.props.newEditorEnabled) {
            sendToStatist('editor.attachment.tap', {hotkey: false});
        }

        this.fileInput.current?.click();
    };

    render() {
        const {formatMessage} = this.props.intl;
        let multiple = true;
        if (isMobileApp()) {
            // iOS WebViews don't upload videos properly in multiple mode
            multiple = false;
        }

        let accept = '';

        if (isIosChrome()) {
            // iOS Chrome can't upload videos at all
            accept = 'image/*';
        }

        const uploadsRemaining = Constants.MAX_UPLOAD_FILES - this.props.fileCount;

        let bodyAction;
        const ariaLabel = formatMessage({id: 'accessibility.button.attachment', defaultMessage: 'attachment'});

        if (this.props.pluginFileUploadMethods?.length === 0) {
            bodyAction = (
                <>
                    <ActionButton
                        id='fileUploadButton'
                        icon={<PaperclipDiagonalNewIcon/>}
                        aria-label={ariaLabel}
                        onClick={this.simulateInputClick}
                        onTouchEnd={this.simulateInputClick}
                    />
                    <input
                        className='file-attachment-menu-item-input'
                        id='fileUploadInput'
                        tabIndex={-1}
                        aria-label={formatMessage(holders.uploadFile)}
                        ref={this.fileInput}
                        type='file'
                        onChange={this.handleChange}
                        onClick={this.handleLocalFileUploaded}
                        multiple={multiple}
                        accept={accept}
                        data-testid='fileUploadInput'
                    />
                </>
            );
        } else {
            const pluginFileUploadMethods = (this.props.pluginFileUploadMethods || []).map((item) => {
                return (
                    <li
                        key={item.pluginId + '_fileuploadpluginmenuitem'}
                        onClick={() => {
                            if (item.action) {
                                item.action(this.checkPluginHooksAndUploadFiles);
                            }
                        }}
                    >
                        <a href='#'>
                            <span className='mr-2'>{item.icon}</span>
                            {item.text}
                        </a>
                    </li>
                );
            });
            bodyAction = (
                <div>
                    <input
                        tabIndex={-1}
                        aria-label={formatMessage(holders.uploadFile)}
                        ref={this.fileInput}
                        type='file'
                        className='file-attachment-menu-item-input'
                        onChange={this.handleChange}
                        onClick={this.handleLocalFileUploaded}
                        multiple={multiple}
                        accept={accept}
                    />
                    <MenuWrapper>
                        <button
                            type='button'
                            aria-label={ariaLabel}
                            className='style--none post-action'
                        >
                            <div
                                id='fileUploadButton'
                                className='icon icon--attachment'
                            >
                                <PaperclipDiagonalNewIcon className='d-flex' />
                            </div>
                        </button>
                        <Menu
                            id='fileUploadOptions'
                            openLeft={true}
                            openUp={true}
                            ariaLabel={formatMessage({
                                id: 'file_upload.menuAriaLabel',
                                defaultMessage: 'Upload type selector',
                            })}
                            customStyles={customStyles}
                        >
                            <li>
                                <a
                                    href='#'
                                    onClick={this.simulateInputClick}
                                    onTouchEnd={this.simulateInputClick}
                                >
                                    <span className='mr-2'>
                                        <i className='fa fa-laptop' />
                                    </span>
                                    {formatMessage({
                                        id: 'yourcomputer',
                                        defaultMessage: 'Your computer',
                                    })}
                                </a>
                            </li>
                            {pluginFileUploadMethods}
                        </Menu>
                    </MenuWrapper>
                </div>
            );
        }

        if (!this.props.canUploadFiles) {
            bodyAction = null;
        }

        return (
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.filesUpload}>
                <div className={uploadsRemaining <= 0 ? ' style--none btn-file__disabled' : 'style--none'}>
                    {bodyAction}
                </div>
            </ShortcutTooltip>
        );
    }
}

const wrappedComponent = injectIntl(FileUpload, {forwardRef: true});
wrappedComponent.displayName = 'injectIntl(FileUpload)';
export default wrappedComponent;
